import { Selectable } from 'kysely'
import { ConvertibleActions, PressReleases, PrFilterKeywords } from './db.js'
import { WarrantSubscriptionPeriod } from './types/index.js'

type WarrantEntityWithNames = Record<
    'instrumentName' | 'companyName' | 'name' | 'instrumentSymbol',
    string | null | undefined
>

const ensureWhitespace = (str?: string | null) => {
    if (!str) return ''

    const match = str.match(/((?<=TO)[0-9])/)

    if (!match) return str

    return str.slice(0, match.index) + ' ' + str.slice(match.index)
}

const ensureTOPrefix = (str: string | null) => {
    if (!str) return ''

    return str.replace(/^([0-9])/, 'TO $1')
}

type INormalizedWarrantName = {
    (warrant: WarrantEntityWithNames, displayType?: 'full' | 'short'): string
    (warrant: Omit<WarrantEntityWithNames, 'instrumentSymbol'>, displayType?: 'full'): string
}

const normalizedName = (name?: string | null) => name?.replace(/ \(publ\)/, '')

export const normalizedWarrantName = ((warrant: WarrantEntityWithNames, displayType: 'full' | 'short' = 'full') => {
    if (!warrant) return ''

    if (displayType === 'short') {
        return ensureWhitespace(warrant.instrumentSymbol) || ensureTOPrefix(ensureWhitespace(warrant.name))
    }

    if (warrant.instrumentName) return ensureWhitespace(warrant.instrumentName)

    return `${normalizedName(warrant.companyName)} ${ensureTOPrefix(ensureWhitespace(warrant.name))}`
}) as INormalizedWarrantName

export const shouldKeepItemByFieldMatch = <I extends Pick<Selectable<PressReleases>, 'title'>, T extends keyof I>(
    filters: Pick<Selectable<PrFilterKeywords>, 'field' | 'negation' | 'phrase'>[],
    item: I,
    field: T,
    restList: Array<I>,
) => {
    const filtersForField = filters.filter((f) => !field || f.field === field)
    const positivePhrases = filtersForField.filter((f) => !f.negation).map((f) => f.phrase)
    const negativePhrases = filtersForField.filter((f) => f.negation).map((f) => f.phrase)

    {
        const match = negativePhrases.find((phrase) =>
            (item[field] as string | null)?.toLowerCase().includes(phrase.toLowerCase()),
        )

        if (match) {
            console.log(`Press release should be filtered due to match on the negative phrase ${match}`, {
                field,
                item,
            })
            return false
        }
    }
    {
        const match = positivePhrases.some((phrase) =>
            (item[field] as string | null)?.toLowerCase().includes(phrase.toLowerCase()),
        )
        if (match) {
            console.log(`Press release should not be filtered due to match on the positive phrase ${match}`, {
                field,
                item,
            })
            return true
        }
    }

    console.log('Press release should be filtered, but to be put in rest list to be checked for further criteria.')

    // default: save the item in the rest list, and remove it from the current list
    restList.push(item)
    return false
}

export const getConvertibleLoanAmount = (convertible: {
    convertibleActions: Array<Pick<Selectable<ConvertibleActions>, 'actionType' | 'loanAmount'>>
}) => {
    if (!convertible.convertibleActions || convertible.convertibleActions.length === 0) return null

    return convertible.convertibleActions.reduce((acc: number, action) => {
        if (action.actionType === 'new_loan' && action.loanAmount !== null) {
            acc += Number(action.loanAmount)
        }
        return acc
    }, 0)
}
/**
 * Find the subscription period closest to a specific date for a warrant.
 * - returns null if no subscription periods are found
 * - if we have a current period, based on subStartDate and subEndDate, return it
 * - if we have no current period, but we have future periods, return the nearest one to current date
 * - if we have no current or future periods, return the last period
 */
export function findSubscriptionPeriodCloseToDate<WSP extends WarrantSubscriptionPeriod>(
    { subscriptionPeriods }: { subscriptionPeriods?: WSP[] } = {},
    dateISOString: string,
) {
    if (!subscriptionPeriods) return null
    // if we have a current period, based on subStartDate and subEndDate, return it
    // if we have no current period, but we have future periods, return the nearest one to current date
    // if we have no current or future periods, return the last period
    const futurePeriods: WSP[] = []
    let currentPeriod: WSP | null = null
    let lastPeriod: WSP | null = null
    subscriptionPeriods.forEach((period) => {
        if (period.subStartDate === null || period.subEndDate === null) {
            futurePeriods.push(period)
            return
        }

        const subStartDate = new Date(period.subStartDate).toISOString()
        const subEndDate = new Date(period.subEndDate).toISOString()

        if (dateISOString >= subStartDate && dateISOString <= subEndDate) {
            currentPeriod = period
        } else if (dateISOString < subStartDate) {
            futurePeriods.push(period)
        } else {
            lastPeriod = period
        }
    })
    if (currentPeriod) return currentPeriod
    if (futurePeriods.length > 0) {
        futurePeriods.sort(
            (a, b) => new Date(a.subStartDate as string).getTime() - new Date(b.subStartDate as string).getTime(),
        )
        return futurePeriods[0]
    }
    return lastPeriod
}

export const getWarrantAmountForPriceAtDate = (
    price: string | number | null,
    totalNumberOfWarrants: number | null,
    subscriptionPeriods: WarrantSubscriptionPeriod[],
    dateISOString: string,
) => {
    if (price === null || isNaN(+price)) return null
    if (totalNumberOfWarrants === null) return null

    const currentPeriod: WarrantSubscriptionPeriod | null = findSubscriptionPeriodCloseToDate(
        { subscriptionPeriods },
        dateISOString,
    )

    if (
        !currentPeriod?.subStartDate ||
        currentPeriod.warrantsRequired === null ||
        currentPeriod.sharesReceived === null
    )
        return null

    const previousPeriods = subscriptionPeriods.filter(
        (period) =>
            period.subStartDate && new Date(period.subStartDate) < new Date(currentPeriod.subStartDate as string),
    )
    const previouslyExercisedWarrants = previousPeriods.reduce(
        (acc, { sharesSubscribed, sharesReceived, warrantsRequired }) => {
            if (sharesSubscribed === null || sharesReceived === null || warrantsRequired === null) return acc

            return acc + (sharesSubscribed * warrantsRequired) / sharesReceived
        },
        0,
    )

    const remainingWarrants = totalNumberOfWarrants - previouslyExercisedWarrants

    return ((+price * remainingWarrants) / currentPeriod.warrantsRequired) * currentPeriod.sharesReceived
}

export const hasSubPeriodPrelPrice = (subPeriod: WarrantSubscriptionPeriod) =>
    subPeriod.warrantSubPrice === null && subPeriod.prelSubPrice !== null
